import {DataObjectId, User} from 'yougile-model'
import {ArrUtil, logger, Util} from 'yougile-util'
import {Sync} from '../../client/sync'
import {ChatSubscribers} from '../../extensions/chat-subscribers'
import {globals} from '../../extensions/globals-base'
import {Sticker} from '../../extensions/sticker/sticker'
import {UserAccess} from '../../extensions/user-access'
import {ShowMessageBox} from '../../types/message-box'
import {ContextObject} from '../context-object'
import {Item} from './item'
import {Items} from './items'
import {UserObj} from './user-obj'

/**
 * @description
 * Manipulating users.
 * Allows to get users’ info:
 * names, emails, online statuses, photos, permissions, …
 * Also allows to change who is in projects, who is assigned to
 * tasks and who is added to chats.
 * Allows to change the way user shown (name, photo, statuses).
 * @descriptionRu
 * Управление пользователями.
 * Позволяет получит информацию о пользователях:
 * Имена, e-mail, статусы, фотографии, разрешения и т.п.
 * Также позволяет менять принадлежность к проектами, кто прикреплён
 * к задаче и кто добавлен в нотификацию в чате.
 * Позволяет изменять отображение пользователя (имя, фото, статусы).
 */
export class Users extends ContextObject {
  public static override registered: Map<DataObjectId, Map<string, Users>> =
    new Map()

  public static init(_showMessageBox: ShowMessageBox): void {
    //
  }

  /**
   * @code
   * Users.listAll();
   * @example
   * // saving all users to variable “all” for the later use
   * var all = Users.listAll();
   * // getting a task
   * var t = Items.get('... task id here');
   * // posting message “Hi!” on behalf of each user in the company
   * all.forEach(function (u) {
   *   Chat.postMessage(t, u, 'Hi!');
   * });
   * @description
   * Returns all users added to the company.
   * @descriptionRu
   * Возвращает всех пользователей, добавленных в компанию.
   */
  public listAll(): UserObj[] {
    return this.context.box.getAllUsers(false).map(Users.fromEntity)
  }

  /**
   * @code
   * Users.get('... email or id here');
   * @example
   * var user1 = Users.get('jack@example.com');
   * var user2 = Users.get('... user id for another user');
   * // ... doing something with “user1” and “user2”
   * @description
   * Returns the user with the specified email or id.
   * @descriptionRu
   * Возвращает пользователя по указанному email или id.
   */
  public get(emailOrId: string): UserObj {
    if (typeof emailOrId !== 'string') {
      return undefined
    }
    emailOrId = Util.cleanEmail(emailOrId)
    return this.context.box
      .getAllUsers(true)
      .map(Users.fromEntity)
      .find((u: UserObj) => u.email === emailOrId || u.id === emailOrId)
  }

  /**
   * @code
   * Users.isInProject(Users.get('... email or id'), Items.get('... id'));
   * @example
   * // getting the project
   * var prj = Items.get('... id');
   * // checking if current user is added to the project
   * if (Users.isInProject(Current.user, pr)) {
   *   Notifier.success('You are in the project!');
   * }
   * @description
   * Returns true if the user is added to the specified project.
   * @descriptionRu
   * Возвращает true, если пользователь добавлен к указанному проекту.
   */
  public isInProject(user: UserObj, project: Item): boolean {
    if (!user || !project) {
      return false
    }
    if (project.type !== 'Project') {
      logger.warn(`${project.name} is not a project! Returning false…`)
      return false
    }
    if (this.context.box.companyId !== project.box.companyId) {
      return false
    }
    return !!UserAccess.getRoleInProject(user.id, Items.toHub(project))
  }

  /**
   * @code
   * Users.addToProject(Users.get('... email or id'), Items.get('... id'));
   * @example
   * var pr = Items.get('... project id here');
   * // adding current user to the project
   * Users.addToProject(Current.user, pr);
   * @description
   * Adds the user to the specified project.
   * @descriptionRu
   * Добавляет пользователя к указанному проекту.
   */
  public addToProject(user: UserObj, project: Item): void {
    if (!user || !project) {
      return
    }
    if (this.context.box.companyId !== project.box.companyId) {
      return
    }
    if (project.type !== 'Project') {
      logger.warn(`${project.name} is not a project! Skipping…`)
      return
    }
    if (!UserAccess.getRoleInProject(user.id, Items.toHub(project))) {
      UserAccess.setRoleInProject(user.id, Items.toHub(project), 'worker')
    }
  }

  /**
   * @code
   * Users.removeFromProject(Users.get('...'), Items.get('... id'));
   * @example
   * var pr = Items.get('... project id here');
   * // removing current user to the project
   * Users.removeFromProject(Current.user, pr);
   * @description
   * Removes the user from the specified project.
   * @descriptionRu
   * Удаляет пользователя из указанного проекта.
   */
  public removeFromProject(user: UserObj, project: Item): void {
    if (!user || !project) {
      return
    }
    if (this.context.box.companyId !== project.box.companyId) {
      return
    }
    if (project.type !== 'Project') {
      logger.warn(`${project.name} is not a project! Skipping…`)
      return
    }
    UserAccess.removeRoleInProject(user.id, Items.toHub(project))
  }

  /**
   * @code
   * Users.listAllInProject(Items.get('... id'));
   * @example
   * var p = Items.get('... project id here');
   * var users = Users.listAllInProject(p);
   * var names = users.map(function (u) {
   *   return u.name;
   * });
   * Notifier.success('Users: ' + names.join() + ' are in the project');
   * @description
   * Returns all users added to the project.
   * @descriptionRu
   * Возвращает список пользователей, добавленных в проект.
   */
  public listAllInProject(project: Item): UserObj[] {
    if (!project) {
      return []
    }
    if (this.context.box.companyId !== project.box.companyId) {
      return []
    }
    return UserAccess.listUsersInProject(Items.toHub(project)).map(
      Users.fromEntity
    )
  }

  /**
   * @code
   * Users.getAssigned(Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * var assigned = Users.getAssigned(t);
   * Notifier.success('User ' + assigned.name + ' assigned to the task');
   *
   * // get all assigned users
   * var allAssigned = Users.getAssigned(t, true); // allAssigned is an array
   * @description
   * Returns the assigned user for the task.
   * @descriptionRu
   * Возвращает назначенного на задачу пользователя.
   */
  public getAssigned(task: Item, returnAll: boolean): UserObj | UserObj[] {
    if (!task || this.context.box.companyId !== task.box.companyId) {
      return undefined
    }
    const hub = Items.toHub(task)
    if (!returnAll) {
      const userId = Sticker.getUsers(hub)[0]
      return userId ? new UserObj(userId, this.context.box) : undefined
    } else {
      const userIds = Sticker.getUsers(hub)
      return userIds.map(uid => new UserObj(uid, this.context.box))
    }
  }

  /**
   * @code
   * Users.isUserAssigned(Users.get('...'), Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * if (Users.isUserAssigned(Current.user, t)) {
   *   Notifier.success('It is your job to complete task ' + t.name);
   * } else {
   *   Notifier.success('Task ' + t.name + ' is someone else’s business');
   * }
   * @description
   * Returns true if the user is assigned to the specified task.
   * @descriptionRu
   * Возвращает true, если пользователь назначен на указанную задачу.
   */
  public isUserAssigned(user: UserObj, task: Item): boolean {
    const users = this.getAssigned(task, true) as UserObj[]
    return !!users.find(u => u.id === user.id)
  }

  /**
   * @code
   * Users.assignUser(Users.get('...'), Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * // assigning the task to the current user
   * Users.assignUser(Current.user, t);
   *
   * // removing assignment
   * Users.assignUser([], t);
   *
   * // adding multiple users
   * Users.assignUser([user1, user2], t);
   * @description
   * Assign a task to the specified user. To remove assignment you can pass
   * undefined to the first argument.
   * @descriptionRu
   * Прикрепление указанного пользователя к задаче. Чтобы открепить
   * пользователя, вы можете передать значние undefined к первому аргументу.
   */
  public assignUser(user: UserObj | UserObj[], task: Item): void {
    if (!task || this.context.box.companyId !== task.box.companyId) {
      return
    }
    const tag = Sticker.getByName(task.box, 'User')
    const hub = Items.toHub(task)
    if (user instanceof Array) {
      if (user.length === 0) {
        delete hub.stickers[tag.id]
      } else {
        hub.stickers[tag.id] = ArrUtil.unique(
          user.map(u => !!u && u.id).filter(id => !!id),
          id => id
        )
      }
    } else {
      if (user) {
        hub.stickers[tag.id] = user.id
      } else {
        delete hub.stickers[tag.id]
      }
    }
    hub.commit()
  }

  /**
   * @code
   * Users.addToChat(Users.get('...'), Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * Users.addToChat(Current.user, t);
   * @description
   * Adding the user to the specified chat (corresponding task is passed).
   * @descriptionRu
   * Добавляет пользователя к указанному чату (соответствующая задача
   * передается).
   */
  public addToChat(user: UserObj, task: Item): void {
    if (!user || !task || this.context.box.companyId !== task.box.companyId) {
      return
    }
    ChatSubscribers.setSubscription(user.id, Items.toHub(task), true)
  }

  /**
   * @code
   * Users.removeFromChat(Users.get('...'), Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * Users.removeFromChat(Current.user, t);
   * @description
   * Removing the user from the specified chat
   * (corresponding task is passed).
   * @descriptionRu
   * Удаление юзера из нотификации по чату
   * (соответствующая задача передается).
   */
  public removeFromChat(user: UserObj, task: Item): void {
    if (!user || !task || this.context.box.companyId !== task.box.companyId) {
      return
    }
    ChatSubscribers.setSubscription(user.id, Items.toHub(task), false)
  }

  /**
   * @code
   * Users.isInChat(Users.get('...'), Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * Users.isInChat(Current.user, t);
   * @description
   * Returns true if the user is added to the specified chat
   * (corresponding task is passed).
   * @descriptionRu
   * Возвращает true, если юзер добавлен в нотификацию по указанному
   * чату (соответствующая задача передается).
   */
  public isInChat(user: UserObj, task: Item): boolean {
    if (!user || !task || this.context.box.companyId !== task.box.companyId) {
      return false
    }
    const h = Items.toHub(task)
    return ChatSubscribers.isSubscribed(user.id, h)
  }

  /**
   * @code
   * Users.listAllInChat(Items.get('... task id'));
   * @example
   * var t = Items.get('... task id');
   * Users.listAllInChat(t);
   * @description
   * Gets all users who are added to the chat.
   * Chat’s task is passed as an argument.
   * @descriptionRu
   * Получает всех пользователей, добавленных в чат.
   * Задача передается как аргумент.
   */
  public listAllInChat(task: Item): UserObj[] {
    if (!task || this.context.box.companyId !== task.box.companyId) {
      return []
    }
    return ChatSubscribers.getSubscribedUserIds(Items.toHub(task)).map(
      (id: DataObjectId) => new UserObj(id, task.box)
    )
  }

  /**
   * @code
   * Users.isHidden = function (user) {
   *   return false;
   * };
   * @description
   * Controls if user should be shown in lists of all users.
   * @descriptionRu
   * Позволяет указывать кто из пользователей показывается
   * в списках пользователей (на странице проектов, в проекте,
   * в списке уведомления по чату, в стикере Пользователь).
   * @example
   * Users.isHidden = function (u) {
   *   // do not show other users to the user
   *   return u.id === Current.user.id;
   * };
   */
  public isHidden: (user: UserObj) => boolean

  /**
   * @todo implement
   * @code
   */
  public onList: () => void

  /**
   * @todo implement
   * @code
   */
  public onListInProject: () => void

  /**
   * @todo implement
   * @code
   */
  public onListInChat: () => void

  /**
   * @todo implement
   * @code
   */
  public onAddToProject: () => void

  /**
   * @todo implement
   * @code
   */
  public onRemoveFromProject: () => void

  /**
   * @todo implement
   * @code
   */
  public onAddToChat: () => void

  /**
   * @todo implement
   * @code
   */
  public onRemoveFromChat: () => void

  /**
   * @todo implement
   * @code
   */
  public onClick: () => void

  /**
   * @todo implement
   * @code
   */
  public onDisplayName: () => void

  public static isHidden(user: UserObj): boolean {
    return !Users.forEachCurrent((i: Users) =>
      i.isHidden ? !i.isHidden(user) : true
    )
  }

  public static fromEntity(user: User): UserObj {
    if (!user || user.companyId !== globals.getCompanyId()) {
      return undefined
    }
    return new UserObj(user.id, Sync.get(user))
  }

  public static get(id: DataObjectId): UserObj {
    return new UserObj(id, Sync.getCurrentSandbox())
  }
}
